import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FormikProps } from "formik";

import { useFormikWithoutDirtyForm } from "../../forms/useFormikWithDirtyForm";
import {
  ResultText,
  SPOUSAL_MAINTENANCE_EMPTY_VALUES,
  SpousalMaintenanceCalculation,
  SpousalMaintenanceFieldVisibility,
  SpousalMaintenanceFormValues,
  SpousalMaintenanceInput,
} from "../SpousalMaintenance.types";
import { buildResultText, calculateSpousalMaintenance } from "../SpousalMaintenance.utils";

interface UseSpousalMaintenanceCalculator {
  formik: FormikProps<SpousalMaintenanceFormValues>;
  visibility: SpousalMaintenanceFieldVisibility;
  input: SpousalMaintenanceInput | null;
  calculation: SpousalMaintenanceCalculation | null;
  resultText: ResultText | null;
}

export function useSpousalMaintenanceCalculator(): UseSpousalMaintenanceCalculator {
  const { t } = useTranslation();

  const formik = useFormikWithoutDirtyForm<SpousalMaintenanceFormValues>({
    initialValues: SPOUSAL_MAINTENANCE_EMPTY_VALUES,
    onSubmit: () => {},
  });

  const { values } = formik;

  const spouseIncomeValue = values.spouseIncome;
  const ownIncomeValue = values.ownIncome;
  const housingValue = values.housingCosts;
  const childrenValue = values.children;

  const hasOwnIncomeYes = values.hasOwnIncome === true;
  const hasOwnIncomeAnswered = values.hasOwnIncome !== null;
  const livingTogetherYes = values.livingTogether === true;
  const livingTogetherAnswered = values.livingTogether !== null;

  const showSpouseIncome = values.isMarried === true;
  const showHasOwnIncome = showSpouseIncome && spouseIncomeValue !== null;
  const showOwnIncome = showHasOwnIncome && hasOwnIncomeYes;
  const ownIncomeReady = hasOwnIncomeAnswered && (!hasOwnIncomeYes || ownIncomeValue !== null);
  const showLivingTogether = showHasOwnIncome && ownIncomeReady;
  const showHousingCosts = showLivingTogether && livingTogetherYes;
  const housingReady = livingTogetherAnswered && (!livingTogetherYes || housingValue !== null);
  const showChildren = showLivingTogether && housingReady;

  const visibility: SpousalMaintenanceFieldVisibility = {
    notMarried: values.isMarried === false,
    showSpouseIncome,
    showHasOwnIncome,
    showOwnIncome,
    showLivingTogether,
    showHousingCosts,
    showChildren,
  };

  const input = useMemo<SpousalMaintenanceInput | null>(() => {
    const canCalculate = visibility.showChildren && childrenValue !== null && spouseIncomeValue !== null;
    if (!canCalculate) {
      return null;
    }

    // Amounts whose gating answer is "Nein" are zeroed here instead of being reset in the form, so an
    // accidental toggle never destroys what the user entered.
    return {
      spouseIncome: spouseIncomeValue,
      ownIncome: hasOwnIncomeYes ? (ownIncomeValue ?? 0) : 0,
      livingTogether: livingTogetherYes,
      housingCosts: livingTogetherYes ? (housingValue ?? 0) : 0,
      children: childrenValue,
    };
  }, [
    visibility.showChildren,
    spouseIncomeValue,
    hasOwnIncomeYes,
    ownIncomeValue,
    livingTogetherYes,
    housingValue,
    childrenValue,
  ]);

  const calculation = useMemo<SpousalMaintenanceCalculation | null>(
    () => (input === null ? null : calculateSpousalMaintenance(input)),
    [input]
  );

  const resultText = useMemo<ResultText | null>(
    () => (input !== null && calculation !== null ? buildResultText(t, input, calculation) : null),
    [t, input, calculation]
  );

  return { formik, visibility, input, calculation, resultText };
}
